import axios from 'axios';
import { message } from 'antd';
import qs from 'qs';
import intl from 'react-intl-universal';
import { getEnv } from '@/config';
import { apiHosts, intlApiHosts } from '@/config/apiHosts';
import { getSwimTag, storage, getCodeInfoFromDomain, isIntl } from '@/utils';
import { errorCodeHandle } from '@/components/ErrorCodeHandle';
import { logoutToLogin } from '@/utils/user';
import {
  parseRequestTimeZone,
  parseResponseTimeZone,
  parseGlobalRequestHeader,
  generateTraceparent,
  parseLongDataTransformResponse,
} from '@/utils/interceptors';
import { IWrapperAxiosInstance } from '@/utils/type';
import { urlPrefix } from './prefix';
import { BizErrorCode, SpecialErrorCode } from './bizCode';

const OTHER_BASE_URL = ['https://m.ctrip.com'];

const env = getEnv();
const swimTag = getSwimTag();
const { apiThirdLevel } = getCodeInfoFromDomain();
const isIntlFlag = isIntl();
const url = isIntlFlag ? intlApiHosts[env].replace('.sg.', `.${apiThirdLevel}.`) : apiHosts[env];

const http = axios.create({
  timeout: 30 * 1000,
  // withCredentials: true, // send cookies when cross-domain equests
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
  },
  transformResponse: [parseLongDataTransformResponse],
}) as IWrapperAxiosInstance;

// Add a request interceptor
http.interceptors.request.use(
  (config) => {
    const newConfig = { ...config };
    const token = storage.get('token');
    const commonCode = storage.get('commonCode');

    if (token) newConfig.headers.Authorization = token; // 用户身份标识
    if (commonCode) newConfig.headers.commonCode = commonCode; // 门店公用码
    if (swimTag) newConfig.headers.swim = swimTag; // 多泳道标识

    // 请求发起时的本机时间
    newConfig.headers['x-request-send-timestamp'] = Date.now();
    // traceparent
    newConfig.headers.traceparent = generateTraceparent();
    // 兼容实例自定义baseURL
    if (!newConfig.baseURL) {
      newConfig.baseURL = `${url}/${config.headers.urlPrefix || urlPrefix}`;
    }
    return newConfig;
  },
  (error) => {
    return Promise.reject(error);
  },
);

http.interceptors.request.use(parseGlobalRequestHeader);
http.interceptors.request.use(parseRequestTimeZone);

// Add a response interceptor
http.interceptors.response.use(
  (response: any) => {
    if (response.data instanceof Blob) {
      // 获取文件名
      const contentDisposition = response.headers['content-disposition'];
      const dispositionArray = contentDisposition?.split(';') || [];
      let filename: any = '';
      for (let i = 0; i < dispositionArray.length; i++) {
        if (dispositionArray[i]?.indexOf('filename') !== -1) {
          const obj = qs.parse(dispositionArray[i]);
          filename = Object.values(obj).pop();
          break;
        }
      }
      response.data.filename = decodeURIComponent(filename);
      return response.data;
    }
    // 兼容其他baseURL返回的数据结构
    if (OTHER_BASE_URL.includes(response.config.baseURL)) {
      return response.data;
    }

    const { code } = response.data;

    if (code === 999) {
      // redirect to login
      logoutToLogin();
      // fix：token失效时 刷新时导致的错误提示
      return new Promise(() => {});
    }
    if (response.data.code !== 200 && response.data.code !== 0) {
      // 不清楚接口只对特殊code进行特殊处理
      if (SpecialErrorCode[response.data.code]) {
        errorCodeHandle(response.data.code, response.data);
        return Promise.reject(response.data);
      }

      // hack codes
      if (BizErrorCode[response.data.code]) {
        return Promise.reject(response.data);
      }

      message.error(
        response.data.msg || response.data.message || intl.get('api_http_service_error'),
      );
      return Promise.reject(response.data);
    }

    // return response.data;
    return parseResponseTimeZone(response);
  },
  (error) => {
    if (['/ctrip/task'].includes(error.response?.config?.url)) {
      message.error(error.response.data.msg || intl.get('app_underversion'));
    } else {
      message.error(intl.get('api_http_network_error'));
    }
    return Promise.reject(error);
  },
);

export default http;
